import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import block from 'bem-cn';
import PT from 'prop-types';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import SVGInline from 'react-svg-inline';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { actions as authActions } from 'features/Auth';

import Modal from 'components/ModalProvider/Modal/Modal';

import crossSVG from 'shared/img/cross-transparent.svg';

import './NotifiesView.scss';
import { useNotificationsObserver } from '../../../../hooks/useNotificationsObserver';

const b = block('notifies-view');

dayjs.extend(customParseFormat);

const NotifiesView = ({ closeFunction, openedNotify, setNotify }) => {
  const dispatch = useDispatch();
  const { notifications } = useSelector(state => state.auth, shallowEqual);
  const { locale } = useSelector(state => state.locale, shallowEqual);
  const hideNotification = (id) => {
    if (id) {
      const hiddenNotifications = JSON.parse(localStorage.getItem('hiddenNotifications') || '[]');
      localStorage.setItem('hiddenNotifications', JSON.stringify([...hiddenNotifications, id]));

      dispatch(authActions.dropNotifications(id));
      dispatch(authActions.removeSeenNotifications(id));

    } else {
      closeFunction();
    }
  };

  useNotificationsObserver({ notifications });

  const handleCrossClick = useCallback(
    (id) => {
      hideNotification(id);
    },
    [closeFunction, dispatch]
  );

  const lessThanTwelveHoursAgo = useMemo(() => {
    return date => dayjs().isBefore(dayjs(date, 'DD.MM.YYYY HH:mm:ss').add(12, 'hours'));
  }, []);

  const itemsRef = useRef(null);

  const [nameLinesCounts, setNameLinesCounts] = useState({});

  const countNameLines = () => {
    const elements = itemsRef.current.querySelectorAll(`.${b('name')}`);
    const linesCounts = {};

    elements.forEach(element => {
      const notificationId = element.closest(`.${b('item')}`).dataset.id;
      const computedStyle = window.getComputedStyle(element);
      const lineHeight = parseInt(computedStyle.lineHeight);
      const paddingTop = parseInt(computedStyle.paddingTop);
      const paddingBottom = parseInt(computedStyle.paddingBottom);
      const totalHeight = element.clientHeight - paddingTop - paddingBottom;
      linesCounts[notificationId] = Math.ceil(totalHeight / lineHeight);
    });

    return linesCounts;
  };

  useEffect(() => {
    const linesCount = countNameLines();
    setNameLinesCounts(linesCount);
  }, [notifications]);

  const isNotifyOpen = openedNotify !== null;

  const items = useMemo(
    () =>
      notifications
        // ?.filter(({ id }) => (openedNotify !== null ? openedNotify === id : true))
        .map(({ id, date, notification }) => {
          const showAllVisible = nameLinesCounts[id] >= 2;

          const openedNotifyObject = {
            date,
            id,
            notification,
          };

          return (
            <div className={b('item')} key={id} data-id={id}>
              <div className={b('item-wrapper')}>
                <div className={b('item-left')}>
                  {lessThanTwelveHoursAgo(date) && <div className={b('new')}>{locale.new}</div>}
                  <p className={b('type')}>{locale.attention}</p>
                </div>
                <div className={b('item-right')}>
                  <p className={b('date')}>{dayjs(date, 'DD.MM.YYYY HH:mm:ss').format('DD.MM.YYYY HH:mm')}</p>
                  <SVGInline
                    className={b('item-cross').toString()}
                    svg={crossSVG}
                    onClick={() => handleCrossClick(id)}
                  />
                </div>
              </div>
              <div className={b('item-wrapper')}>
                <p className={b('name')}>{notification}</p>
              </div>
              {showAllVisible && (
                <div onClick={() => {
                  setNotify(openedNotifyObject)
                }} className={b('item-wrapper')}>
                  <p className={b('show-all')}>{locale.showAll}</p>
                </div>
              )}
            </div>
          );
        }),
    [notifications, locale, lessThanTwelveHoursAgo, handleCrossClick, nameLinesCounts, openedNotify],
  );

  return (
    <>
      {isNotifyOpen && (
        <Modal
          closeFunction={() => handleCrossClick()}
          content={
            <div className={b('item', { openedItem: isNotifyOpen })} key={openedNotify.id} data-id={openedNotify.id}>
              <div className={b('item-wrapper')}>
                <div className={b('item-left')}>
                  {lessThanTwelveHoursAgo(openedNotify.date) && <div className={b('new')}>{locale.new}</div>}
                  <p className={b('type')}>{locale.attention}</p>
                </div>
                <div className={b('item-right')}>
                  <p className={b('date')}>
                    {dayjs(openedNotify.date, 'DD.MM.YYYY HH:mm:ss').format('DD.MM.YYYY HH:mm')}
                  </p>
                  <SVGInline className={b('item-cross').toString()} svg={crossSVG} onClick={() => setNotify(null)} />
                </div>
              </div>
              <div className={b('item-wrapper')}>
                <p className={b('name')}>{openedNotify.notification}</p>
              </div>
            </div>
          }
        />
      )}
      <div className={b({ hide: isNotifyOpen })} ref={itemsRef}>
        <div className={b('header')}>
          <p className={b('title')}>{`${locale.notification}: ${notifications.length}`}</p>
          <SVGInline className={b('cross').toString()} svg={crossSVG} onClick={() => handleCrossClick()} />
        </div>
        <div className={b('items')}>{items}</div>
      </div>
    </>
  );
};

NotifiesView.propTypes = {
  closeFunction: PT.func,
};

export default NotifiesView;
